/**
 * The engine's run lifecycle events, delivered to the run store.
 *
 * `useWebSocketEvents` is mounted once, at the app shell, and owns the socket;
 * `usePipelineRuns` is the store that holds runs but has no socket of its own.
 * This registry is the one wire between them: the dispatcher publishes the run
 * id an event named, and the store refetches that run if it is tracking it.
 *
 * A module-level registry rather than a window event, for the same reason
 * `castEvents` uses one: subscribers are plain typed functions, and a test can
 * publish without going through the DOM.
 */

export interface RunLifecycleEvent {
  runId: string;
  eventType: string;
}

/**
 * The events that mean a run's STATE moved — the ones worth one refetch.
 *
 * Deliberately narrower than the dispatcher's PIPELINE_LIFECYCLE_EVENTS: the
 * high-frequency progress chatter (`GeneratorProgress`, `ReviewTrace`) says
 * nothing new about the run itself, and refetching on it would turn a busy run
 * into a request flood.
 */
export const RUN_STATE_EVENTS = new Set<string>([
  'PipelineStarted',
  'CheckpointStarted',
  'CheckpointCompleted',
  'CheckpointFailed',
  'PipelinePaused',
  'PipelineCompleted',
  'PipelineCancelled',
  'DistributorCompleted',
  'ReviewVerdict',
  // the completion hook links the clip after PipelineCompleted
  'ManifestClipCreated',
  // an agent saved a turn (type "saved"): the run carries it in its agent
  // log. Its other AgentProgress events are per-step chatter (see below).
  'AgentProgress',
  // a director run's graph grew or changed, or the director is waiting on the
  // user: the graph view is read again (a skip from a pause sends only the first).
  'PipelineGraphChanged',
  'PipelineAwaitingDirector',
]);

type RunLifecycleListener = (event: RunLifecycleEvent) => void;

const listeners = new Set<RunLifecycleListener>();

/** Subscribe to every run lifecycle event, whatever run it is for. Returns the unsubscribe. */
export const subscribeRunLifecycle = (listener: RunLifecycleListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const publishRunLifecycle = (event: RunLifecycleEvent): void => {
  Array.from(listeners).forEach((listener) => listener(event));
};

/**
 * Reads the run id off an engine event body — the `data` object the dispatcher
 * already unwrapped. `PipelineStarted` ships the whole run rather than a
 * `run_id` envelope, which is why `id` is the second reading.
 *
 * Returns null for an event that names no run, or one this store has no reason
 * to act on.
 */
export const runLifecycleFromEvent = (
  eventType: string,
  payload: Record<string, unknown>
): RunLifecycleEvent | null => {
  if (!RUN_STATE_EVENTS.has(eventType)) return null;
  if (eventType === 'AgentProgress' && payload.type !== 'saved') return null;
  const raw = payload.run_id ?? payload.id;
  const runId = typeof raw === 'string' ? raw.trim() : '';
  if (!runId) return null;
  return { runId, eventType };
};
